import React from 'react';
import { RiInformationLine, RiLockLine, RiRefreshLine } from 'react-icons/ri';
import elexerMascot from '../../Assets/Elexer_mascot_happy.png';
import ChatTable from './ChatTable';
import ChatChart from './ChatChart';
import ChatInsights from './ChatInsights';
import ChatReportDownload from './ChatReportDownload';
import MarkdownContent from './MarkdownContent';
import { MODULE_META, SUGGESTED_QUESTIONS, SUPPORTED_MODULES } from './constants';

function transportErrorCopy(error) {
    const status = error && error.status;
    const code = error && error.error;

    if (status === 401 || status === 403) {
        return 'Your dashboard session could not be verified. Please log in again.';
    }
    if (status === 400) {
        return "The AI service isn't fully configured. Please contact your administrator.";
    }
    if (status === 404) {
        return "Your account wasn't recognized by the AI service. Try re-logging in.";
    }
    if (status === 429 || code === 'query_in_progress') {
        return 'Please wait for the current question to finish.';
    }
    if (status === 503 && code === 'ai_chat_not_configured') {
        return 'The AI assistant has not been configured for this environment.';
    }
    return 'The AI service is unreachable right now.';
}

const AssistantAvatar = () => (
    <span className="ai-chat-avatar" aria-hidden="true">
        <img src={elexerMascot} alt="" />
    </span>
);

const RetryButton = ({ question, onRetry, disabled }) => (
    <button
        type="button"
        className="ai-chat-retry"
        onClick={() => onRetry(question)}
        disabled={disabled}
    >
        <RiRefreshLine /> Retry
    </button>
);

const ChatMessage = ({ message, onRetry, onSuggestion, pending, suggestLang = 'en' }) => {
    if (message.role === 'user') {
        return (
            <div className="ai-chat-message ai-chat-message--user">
                <div className="ai-chat-user-bubble">{message.text}</div>
            </div>
        );
    }

    if (message.transportError) {
        const retryable = message.transportError.status !== 429;
        return (
            <div className="ai-chat-message ai-chat-message--assistant">
                <AssistantAvatar />
                <div className="ai-chat-response-card ai-chat-response-card--error">
                    <p>{transportErrorCopy(message.transportError)}</p>
                    {retryable && (
                        <RetryButton
                            question={message.originalQuestion}
                            onRetry={onRetry}
                            disabled={pending}
                        />
                    )}
                </div>
            </div>
        );
    }

    const data = message.data || {};
    const moduleMeta = MODULE_META[data.module];
    const isUnrecognized = data.error === 'unrecognized_module';
    const isAccessDenied = data.error === 'access_denied';
    const isGenericError = Boolean(data.error && !isUnrecognized && !isAccessDenied);
    const cardClass = isAccessDenied
        ? 'ai-chat-response-card--warning'
        : isUnrecognized
            ? 'ai-chat-response-card--info'
            : isGenericError
                ? 'ai-chat-response-card--error'
                : '';

    const validTable = data.table
        && Array.isArray(data.table.columns)
        && Array.isArray(data.table.rows)
        && data.table.columns.length > 0;

    return (
        <div className="ai-chat-message ai-chat-message--assistant">
            <AssistantAvatar />
            <article className={`ai-chat-response-card ${cardClass}`}>
                {moduleMeta && data.module !== 'unknown' && (
                    <div
                        className="ai-chat-module-badge"
                        style={{ '--ai-chat-module-color': moduleMeta.color }}
                    >
                        {data.access_granted === false && <RiLockLine aria-hidden="true" />}
                        {moduleMeta.label}
                    </div>
                )}

                {data.response ? (
                    <MarkdownContent
                        className="ai-chat-response-text"
                        content={data.response}
                    />
                ) : (
                    <p className="ai-chat-response-text"><em>(no summary provided)</em></p>
                )}

                {isUnrecognized && (
                    <div className="ai-chat-state-detail">
                        <RiInformationLine aria-hidden="true" />
                        <div>
                            <strong>Supported modules</strong>
                            <p>{SUPPORTED_MODULES.join(', ')}</p>
                            <div className="ai-chat-suggestions ai-chat-suggestions--compact">
                                {(SUGGESTED_QUESTIONS[suggestLang] || SUGGESTED_QUESTIONS.en).slice(0, 4).map(question => (
                                    <button
                                        type="button"
                                        key={question}
                                        onClick={() => onSuggestion(question)}
                                        disabled={pending}
                                    >
                                        {question}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {isAccessDenied && (
                    <div className="ai-chat-state-detail">
                        <RiLockLine aria-hidden="true" />
                        <span>
                            Your role can't query the {moduleMeta ? moduleMeta.label : 'requested'} module.
                            {' '}Contact an admin if you need access.
                        </span>
                    </div>
                )}

                {isGenericError && (
                    <RetryButton
                        question={message.originalQuestion}
                        onRetry={onRetry}
                        disabled={pending}
                    />
                )}

                {validTable && <ChatTable table={data.table} />}
                {data.chart_json && <ChatChart chartJson={data.chart_json} />}
                {data.report_path && <ChatReportDownload reportPath={data.report_path} />}
                {Array.isArray(data.insights) && data.insights.length > 0 && (
                    <ChatInsights insights={data.insights} />
                )}
            </article>
        </div>
    );
};

export default ChatMessage;
